/**
 * Purpose: decide which Design-map dialog a dropped file should open.
 *
 * Input:  files from a map drop (name + optional MIME type).
 * Output: Import geometry for IFC, blueprint underlay for PDF, else AI import
 *         for remaining geometry extensions.
 * Consumers: {@link Map3d} drop handler.
 */

import {
  getGeometryImportCadKind,
  getGeometryImportExtension,
} from '@shared/helpers/design/geometry-import-capabilities.helpers';

/** Minimal file shape so tests do not need a browser {@link File}. */
export type MapDropFile = {
  name: string;
  type?: string;
};

export type MapDropTarget<T extends MapDropFile> =
  | { kind: 'geometry-import'; file: T }
  | { kind: 'blueprint-underlay'; file: T };

/**
 * True when the file is a PDF by MIME type or `.pdf` extension.
 */
export const isPdfDropFile = (file: MapDropFile): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/**
 * True when the file is IFC or ifcZIP (CAD source-text IFC family).
 */
export const isIfcGeometryDropFile = (file: MapDropFile): boolean =>
  getGeometryImportCadKind(file.name) === 'ifc';

export type ResolveMapDropTargetOptions = {
  /** When false, IFC/CAD drops are ignored so a mixed PDF drop can still open. */
  allowGeometryImport?: boolean;
};

/**
 * Pick the dialog for a map file drop.
 * IFC wins over PDF when geometry import is allowed; otherwise PDF wins.
 */
export const resolveMapDropTarget = <T extends MapDropFile>(
  files: readonly T[],
  options: ResolveMapDropTargetOptions = {},
): MapDropTarget<T> | undefined => {
  const allowGeometryImport = options.allowGeometryImport ?? true;
  const ifc = files.find(isIfcGeometryDropFile);
  if (ifc && allowGeometryImport) return { kind: 'geometry-import', file: ifc };

  const pdf = files.find(isPdfDropFile);
  if (pdf) return { kind: 'blueprint-underlay', file: pdf };

  if (!allowGeometryImport) return undefined;

  // DWG/DXF/RVT: keep the AI dialog so it can explain unsupported binaries.
  const geometry = files.find(
    (file) => getGeometryImportExtension(file.name) !== undefined,
  );
  if (geometry) return { kind: 'geometry-import', file: geometry };

  return undefined;
};
